import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Building2, ChevronUp } from 'lucide-react';
import { useT, type MessageKey } from '../../i18n';
import type { ManagedOrganization } from './ClubContext';
import { useEscapeToClose } from './mobileUi';

// ─────────────────────────────────────────────────────────────────────
// «ADMINISTRASJON» — the way into an organization's portal, on the desktop
// sidebar (owner, 2026-10-16).
//
// #848 put the organization door inside the 📍 club sheet. That works, and on
// a phone it stays there — but the 📍 selector answers «which club am I
// PLAYING at» (`/me/clubs`), and administering is a different question
// (`/auth/scopes`). On a desktop the two get two controls, so somebody who
// runs a centre does not have to look for their workspace inside a menu about
// where they play.
//
// - Shown from the rail width up (the phone keeps the sheet's entry), and only
//   when the account administers at least one organization.
// - The list is the scopes the app already holds; entering one calls the
//   existing `onEnterOrganization`. Nothing here grants anything — the server
//   decides on every request inside the portal — and nothing here depends on
//   player membership: an administrator with no roster row sees it too.
// - The native shell passes no organizations (it has no way back from the
//   portal), so this never appears there.
// ─────────────────────────────────────────────────────────────────────

const KIND_KEY: Record<string, MessageKey> = {
  CENTRE: 'account.kind.CENTRE',
  CLUB: 'account.kind.CLUB',
};

export interface AdminMenuProps {
  organizations: ManagedOrganization[];
  onEnterOrganization: (tenantId: string) => void;
}

export function AdminMenu({ organizations, onEnterOrganization }: AdminMenuProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) trigger.current?.focus();
  };
  useEscapeToClose(open ? () => close(true) : null);

  // Outside a pointer press, the menu goes away — it is a menu, not a dialog.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Opening moves focus to the first organization, as a menu does.
  useEffect(() => {
    if (open) menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const items = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? items.length - 1
          : e.key === 'ArrowDown'
            ? (at + 1) % items.length
            : (at - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const title = t('mobile.admin.title');
  return (
    <div ref={wrap} className="relative w-full">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={title}
        className={
          'w-full flex items-center justify-center gap-2.5 rounded-2xl p-2 text-[var(--ink-3)] ' +
          'hover:bg-[#eef0ea] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0f1611] ' +
          '@wide:justify-start @wide:px-3 @wide:py-2.5 @wide:border @wide:border-[#e7eae3] @wide:bg-[#f7f8f5] @wide:text-[#0f1611]'
        }
      >
        <Building2 size={18} className="flex-shrink-0" />
        <span className="hidden @wide:block flex-1 min-w-0 text-left text-[14px] font-semibold truncate">
          {title}
        </span>
        <ChevronUp
          size={15}
          className={`hidden @wide:block flex-shrink-0 transition-transform ${open ? '' : 'rotate-180'}`}
        />
      </button>
      {open && (
        <div
          ref={menu}
          id={menuId}
          role="menu"
          aria-label={title}
          // Focus lives on the items (the arrow keys move it between them), so
          // the menu itself is focusable but never a Tab stop (#900).
          tabIndex={-1}
          onKeyDown={onMenuKey}
          // RAIL: a labelled flyout to the right of the 80 px rail. WIDE: it
          // opens upwards inside the sidebar, the width of the control.
          className={
            'absolute z-40 bottom-0 left-full ml-3 w-72 rounded-2xl p-2 ' +
            '@wide:left-0 @wide:right-0 @wide:ml-0 @wide:w-auto @wide:bottom-full @wide:mb-2'
          }
          style={{
            background: '#fff',
            border: '1px solid #e7eae3',
            boxShadow: '0 12px 32px rgba(15,22,17,0.14)',
          }}
        >
          <div className="px-2 pt-1 pb-2">
            <div className="font-display font-semibold text-ink" style={{ fontSize: 16 }}>
              {title}
            </div>
            <div className="text-[11px] text-ink-3">{t('mobile.admin.lead')}</div>
          </div>
          {organizations.map((o) => (
            <button
              key={o.tenantId}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onEnterOrganization(o.tenantId);
              }}
              className="w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-[#f7f8f5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0f1611]"
            >
              <span
                className="flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ width: 32, height: 32, background: '#eef0ea', color: '#0f1611' }}
              >
                <Building2 size={15} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-ink truncate">{o.name}</span>
                {KIND_KEY[o.kind] && (
                  <span className="block text-[11px] text-ink-3">{t(KIND_KEY[o.kind])}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
